/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { requestExampleDataset } from '@/api/eeCheckApi';
import { createStoredPlotlyRequest } from '@/config/eeCheckConfig';
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import type { StoredPlotlyRequest, StoredPlotlyResponse } from '@/types/eeCheck';
import StoredPlotlyFigure from '@/components/charts/StoredPlotlyFigure';

interface Api2StoredPlotlyPlotProps {
    minWidth?: CSSProperties['minWidth'];
    reference?: 'base_load' | 'heating_energy' | 'thermal_conflict';
    range?: string;
    request?: StoredPlotlyRequest;
    textOverrides?: {
        legendTitle?: string;
        traceNames?: Record<string, string>;
        xAxisTitle?: string;
        yAxisTitle?: string;
    };
    title?: string;
    urn?: string;
}

function Api2StoredPlotlyPlot({
    minWidth,
    reference = 'base_load',
    range,
    request,
    textOverrides,
    title,
    urn,
}: Api2StoredPlotlyPlotProps) {
    const { t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [figure, setFigure] = useState<StoredPlotlyResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const requestSignature = JSON.stringify(request ?? null);
    const hasCustomRequest = request !== undefined;
    const resolvedFigure = useMemo(() => {
        if (!figure) {
            return figure;
        }

        const currentTopMargin =
            typeof figure.layout?.margin?.t === 'number'
                ? figure.layout.margin.t
                : 0;

        return {
            ...figure,
            data: figure.data?.map((trace) => {
                const translatedName = trace.name && textOverrides?.traceNames?.[trace.name];

                return translatedName ? { ...trace, name: translatedName } : trace;
            }),
            layout: {
                ...(figure.layout ?? {}),
                ...(title
                    ? {
                        title: {
                            text: title,
                            x: 0.5,
                            xanchor: 'center' as const,
                        },
                    }
                    : {}),
                ...(textOverrides?.legendTitle
                    ? {
                        legend: {
                            ...(figure.layout?.legend ?? {}),
                            title: { text: textOverrides.legendTitle },
                        },
                    }
                    : {}),
                ...(textOverrides?.xAxisTitle
                    ? {
                        xaxis: {
                            ...(figure.layout?.xaxis ?? {}),
                            title: { text: textOverrides.xAxisTitle },
                        },
                    }
                    : {}),
                ...(textOverrides?.yAxisTitle
                    ? {
                        yaxis: {
                            ...(figure.layout?.yaxis ?? {}),
                            title: { text: textOverrides.yAxisTitle },
                        },
                    }
                    : {}),
                margin: {
                    ...(figure.layout?.margin ?? {}),
                    ...(title ? { t: Math.max(currentTopMargin, 56) } : {}),
                },
            },
        };
    }, [figure, textOverrides, title]);

    useEffect(() => {
        if (isSessionExpired) {
            setIsLoading(false);
            return;
        }

        if (!accessToken || (!hasCustomRequest && (!urn || !range))) {
            setFigure(null);
            setIsLoading(false);
            setErrorMessage(t('Missing Plotly request parameters.'));
            return;
        }

        const payload = hasCustomRequest
            ? JSON.parse(requestSignature) as StoredPlotlyRequest
            : createStoredPlotlyRequest(urn ?? '', reference, range ?? '');
        let isCancelled = false;

        async function loadFigure(): Promise<void> {
            setIsLoading(true);
            setLoadingLabel(t('Loading API request...'));
            setErrorMessage('');

            try {
                const { data } = await requestExampleDataset(accessToken, payload, ({ currentRequest, totalRequests }) => {
                    setLoadingLabel(
                        totalRequests > 1
                            ? t('Loading API request {current}/{total}', {
                                current: String(currentRequest),
                                total: String(totalRequests),
                            })
                            : t('Loading API request...'),
                    );
                });

                if (isCancelled) {
                    return;
                }

                setFigure(data);
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                if (isExpiredSessionError(error)) {
                    onSessionExpired();
                }

                setFigure(null);
                setErrorMessage(
                    error instanceof Error ? error.message : t('Plotly request failed.'),
                );
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                    setLoadingLabel('');
                }
            }
        }

        void loadFigure();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, hasCustomRequest, isSessionExpired, onSessionExpired, range, reference, requestSignature, urn]);

    return (
        <StoredPlotlyFigure
            errorMessage={errorMessage}
            figure={resolvedFigure}
            fillParent
            isLoading={isLoading}
            loadingLabel={loadingLabel}
            minWidth={minWidth}
        />
    );
}

export default Api2StoredPlotlyPlot;
